import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Zap, Trash2 } from "lucide-react";

export default function Activations() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const refresh = () =>
    client.get("/activations").then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await client.post("/activations", { name });
      toast.success("Activation created");
      nav(`/activations/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await client.delete(`/activations/${deleting.id}`);
      toast.success("Activation deleted");
      setDeleting(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setDeleteBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-10" data-testid="activations-page">
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <p className="data-label mb-2">MHQ · ACTIVATIONS</p>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Activations</h1>
          <p className="mt-2 text-sm text-muted-foreground">Per-campaign dynamic parameter sets exported to the CMS.</p>
        </div>
        <Button data-testid="new-activation-btn" className="gap-1.5" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> New activation
        </Button>
      </div>

      {!items && <Skeleton className="h-64" />}
      {items && items.length === 0 && (
        <Card className="p-12 text-center text-sm text-muted-foreground" data-testid="activations-empty">
          <Zap className="mx-auto mb-3 h-8 w-8" strokeWidth={1.5} />
          No activations yet. Create one to get started.
        </Card>
      )}
      {items && items.length > 0 && (
        <Card className="overflow-hidden p-0">
          <table className="min-w-full text-sm" data-testid="activations-table">
            <thead className="thead">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Active</th>
                <th className="px-4 py-2 text-left">Dealers</th>
                <th className="px-4 py-2 text-left">Params</th>
                <th className="px-4 py-2 text-left">Owner</th>
                <th className="px-4 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="row-hover border-t border-border" data-testid={`activation-row-${a.id}`}>
                  <td className="cursor-pointer px-4 py-2 font-medium" onClick={() => nav(`/activations/${a.id}`)}>
                    <span className="inline-flex items-center gap-2">
                      {a.name}
                      {a.imported_from_feed && (
                        <Badge variant="secondary" className="font-mono text-[10px] font-normal" data-testid={`activation-feed-import-badge-${a.id}`}>
                          Feed import
                        </Badge>
                      )}
                    </span>
                  </td>
                  <td className="cursor-pointer px-4 py-2 font-mono text-xs" onClick={() => nav(`/activations/${a.id}`)}>{a.active_from || "—"} → {a.active_to || "—"}</td>
                  <td className="cursor-pointer px-4 py-2 font-mono text-xs tabular-nums" onClick={() => nav(`/activations/${a.id}`)}>{a.dealer_ids?.length ?? 0}</td>
                  <td className="cursor-pointer px-4 py-2 font-mono text-xs tabular-nums" onClick={() => nav(`/activations/${a.id}`)}>{a.parameter_names?.length ?? 0}</td>
                  <td className="cursor-pointer px-4 py-2 text-xs text-muted-foreground" onClick={() => nav(`/activations/${a.id}`)}>{a.creator_owner_email || "—"}</td>
                  <td className="cursor-pointer px-4 py-2 font-mono text-[10px] text-muted-foreground" onClick={() => nav(`/activations/${a.id}`)}>{a.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`activation-delete-btn-${a.id}`}
                      onClick={(e) => { e.stopPropagation(); setDeleting(a); }}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="new-activation-dialog">
          <DialogHeader><DialogTitle>New activation</DialogTitle></DialogHeader>
          <Input data-testid="new-activation-name" placeholder="Name (e.g. Sommermailing 2026)" value={name} onChange={(e) => setName(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button data-testid="new-activation-save" onClick={create} disabled={saving || !name.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && !deleteBusy && setDeleting(null)}>
        <AlertDialogContent data-testid="activation-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &quot;{deleting?.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes this activation and all its dealer + parameter assignments. External CMS calls referencing it will start returning <code>404</code>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleteBusy}
              data-testid="confirm-activation-delete-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
